<template>
  <div class="p-4 sm:p-6 md:p-8">
    <h1 class="text-3xl font-bold mb-2 text-center text-gray-800">Upload Student Pages</h1>
    <p class="text-center text-gray-600 mb-6">
      Select a student, then drag & drop files or click to upload. On mobile, you can take a picture directly.
    </p>

    <!-- 1. Student Selection Controls -->
    <div class="max-w-xl mx-auto bg-white p-4 rounded-lg shadow-md mb-6 flex items-center justify-between space-x-4">
      <div class="flex-grow">
        <label for="student-select" class="block text-sm font-medium text-gray-700 mb-1">Select Student</label>
        <select id="student-select" v-model="selectedStudentId" class="w-full p-2 border border-gray-300 rounded-md">
          <option v-if="students.length === 0 && !authError" disabled value="">Loading students...</option>
          <option v-if="authError" disabled value="">Authentication failed</option>
          <option v-for="student in students" :key="student.id" :value="student.id">
            {{ student.name }}
          </option>
        </select>
      </div>
      <div class="pt-5">
        <button @click="addStudent" class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition whitespace-nowrap">
          + Add Student
        </button>
      </div>
    </div>

    <!-- 2. File Drop Zone -->
    <div class="max-w-xl mx-auto">
      <label
          class="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer transition-colors"
          :class="isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleFileDrop"
      >
        <div class="flex flex-col items-center justify-center pt-5 pb-6">
          <svg class="w-10 h-10 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-4-4V6a4 4 0 014-4h10a4 4 0 014 4v6a4 4 0 01-4 4H7z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m3-3H7"></path></svg>
          <p class="mb-2 text-sm text-gray-500"><span class="font-semibold">Click to upload</span> or drag and drop</p>
          <p class="text-xs text-gray-500">Images will be resized before upload</p>
          <p class="text-xs text-gray-500 mt-2">(On mobile, this will open your camera)</p>
        </div>
        <input ref="fileInput" type="file" class="hidden" @change="handleFileSelect" multiple accept="image/png, image/jpeg, application/pdf" />
      </label>
    </div>

    <!-- 3. Upload Status and Error Messages -->
    <div v-if="statusMessage" class="max-w-xl mx-auto mt-4 p-4 rounded-md" :class="statusMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
      <p class="font-semibold">{{ statusMessage.title }}</p>
      <p class="text-sm">{{ statusMessage.message }}</p>
    </div>

    <!-- Hidden canvas element used for resizing images -->
    <canvas ref="resizeCanvas" style="display: none;"></canvas>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import axios from '../services/axiosInstance'
import SockJS from 'sockjs-client'
import Stomp from 'stompjs'

const testId = ref(null)
const token = ref(null)
const students = ref([])
const selectedStudentId = ref('')
const fileInput = ref(null)
const isDragging = ref(false)
const statusMessage = ref(null)
const authError = ref(false)
const resizeCanvas = ref(null)
const stompClient = ref(null)

onMounted(async () => {
  parseUrlParams()
  if (!testId.value) {
    statusMessage.value = { type: 'error', title: 'Configuration Error', message: 'Test ID is missing from the URL.' }
    authError.value = true
    return
  }
  const loginSuccess = await autoLogin()
  if (loginSuccess) {
    await fetchStudents()
    setupWebSocket()
  }
})

onBeforeUnmount(() => {
  if (stompClient.value && stompClient.value.connected) {
    stompClient.value.disconnect(() => {
      console.log('WebSocket disconnected.')
    })
  }
})

const parseUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  testId.value = urlParams.get('testId')
  token.value = urlParams.get('token')
}

const autoLogin = async () => {
  if (!token.value) {
    console.log('No auth token in URL, assuming existing session.')
    return true
  }
  try {
    await axios.post('/auto-login', { token: token.value })
    console.log('Auto-login successful.')
    return true
  } catch (e) {
    console.error('Auto-login failed:', e)
    statusMessage.value = { type: 'error', title: 'Authentication Failed', message: 'Your link is invalid or has expired. Please scan a new QR code.' }
    authError.value = true
    return false
  }
}

const fetchStudents = async () => {
  try {
    const { data } = await axios.get(`/tests/${testId.value}/students`)
    students.value = data || []
    if (students.value.length > 0 && !selectedStudentId.value) {
      selectedStudentId.value = students.value[0].id
    }
  } catch (e) {
    console.error('Error fetching students:', e)
    if (!token.value) {
      statusMessage.value = { type: 'error', title: 'Authentication Error', message: 'You are not logged in. Please access this page via the QR code.' }
    }
    authError.value = true
  }
}

const addStudent = async () => {
  try {
    // We only send the request. The WebSocket will handle adding the new student to the list.
    await axios.post(`/tests/${testId.value}/students`)
  } catch (e) {
    console.error('Error adding student:', e)
    statusMessage.value = { type: 'error', title: 'Error', message: 'Could not add a new student.' }
  }
}

const setupWebSocket = () => {
  const socket = new SockJS(process.env.VUE_APP_WS_URL)
  stompClient.value = Stomp.over(socket)
  stompClient.value.debug = null
  stompClient.value.connect({}, () => {
    if (!testId.value) return
    stompClient.value.subscribe(`/topic/tests/${testId.value}/status`, (message) => {
      const body = JSON.parse(message.body)
      if (!body.student || !body.student.id) {
        console.warn('Received WebSocket message without valid student data:', body)
        return
      }
      const studentId = body.student.id
      const studentIndex = students.value.findIndex(s => s.id === studentId)

      if (body.type === 'student_updated') {
        if (studentIndex !== -1) {
          console.log(`Updating name for student ${studentId} to "${body.student.name}"`)
          students.value[studentIndex].name = body.student.name
        }
      } else if (body.type === 'todo') { // Handles adding new students
        if (studentIndex === -1) {
          console.log(`Adding new student: ${body.student.name}`)
          students.value.push(body.student)
          selectedStudentId.value = body.student.id
        }
      }
    })
  }, (error) => {
    console.error('WebSocket connection error:', error)
  })
}

const handleFileSelect = (event) => {
  const files = event.target.files
  if (files && files.length > 0) {
    uploadFiles(files)
  }
}

const handleFileDrop = (event) => {
  isDragging.value = false
  const files = event.dataTransfer.files
  if (files && files.length > 0) {
    uploadFiles(files)
  }
}

const resizeImage = (file) => {
  return new Promise((resolve, reject) => {
    const maxDimension = 2048
    const img = new Image()
    img.onload = () => {
      let { width, height } = img
      if (width <= maxDimension && height <= maxDimension) {
        resolve(file)
        return
      }
      if (width > height) {
        height = Math.round(height * (maxDimension / width))
        width = maxDimension
      } else {
        width = Math.round(width * (maxDimension / height))
        height = maxDimension
      }
      const canvas = resizeCanvas.value
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)
      ctx.canvas.toBlob((blob) => {
        if (blob) {
          resolve(new File([blob], file.name, { type: 'image/jpeg' }))
        } else {
          reject(new Error('Canvas to Blob conversion failed.'))
        }
      }, 'image/jpeg', 0.9)
    }
    img.onerror = (err) => reject(err)
    img.src = URL.createObjectURL(file)
  })
}

const uploadFiles = async (fileList) => {
  if (!selectedStudentId.value) {
    alert('Please select a student before uploading.')
    return
  }
  if (authError.value) {
    alert('Cannot upload files due to an authentication error.')
    return
  }
  const filesToUpload = Array.from(fileList)
  const totalFiles = filesToUpload.length
  let successCount = 0
  statusMessage.value = { type: 'info', title: 'Processing...', message: `Preparing ${totalFiles} file(s) for upload...` }
  for (const file of filesToUpload) {
    const formData = new FormData()
    try {
      let fileToUpload = file
      if (file.type.startsWith('image/')) {
        fileToUpload = await resizeImage(file)
      }
      formData.append('file', fileToUpload)
      statusMessage.value = { type: 'info', title: 'Uploading...', message: `Uploading "${file.name}"...` }
      const endpoint = `/tests/${testId.value}/students/${selectedStudentId.value}/upload`
      await axios.post(endpoint, formData)
      successCount++
    } catch (error) {
      console.error('Upload error for file:', file.name, error)
      statusMessage.value = {
        type: 'error',
        title: 'Upload Failed',
        message: `An error occurred while uploading "${file.name}". Please try again.`
      }
      return
    }
  }
  statusMessage.value = {
    type: 'success',
    title: 'Upload Complete',
    message: `${successCount} of ${totalFiles} file(s) uploaded successfully.`
  }
  setTimeout(() => {
    statusMessage.value = null
  }, 5000)
}
</script>

<style scoped>
.handwriting-font {
  font-family: 'Indie Flower', cursive;
}
</style>
